import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { FileSignature, CheckCircle2, ShieldCheck, Eraser, FileText } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/sign`;

export default function SignDocument() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const [docHtml, setDocHtml] = useState(null);
  const [read, setRead] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const drawn = useRef(false);

  useEffect(() => {
    axios.get(`${BASE}/${token}`).then((r) => { setInfo(r.data); setName(r.data.client_name || ""); })
      .catch((e) => setErr(e.response?.data?.detail || "This signing link is not valid or has expired."));
    axios.get(`${BASE}/${token}/content`).then((r) => setDocHtml(r.data.body_html || null)).catch(() => {});
  }, [token]);

  const pos = (e) => {
    const c = canvasRef.current; const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - rect.left) * (c.width / rect.width), y: (t.clientY - rect.top) * (c.height / rect.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const { x, y } = pos(e); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => {
    if (!drawing.current) return; e.preventDefault();
    const ctx = canvasRef.current.getContext("2d"); const { x, y } = pos(e);
    ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#0b1b2b"; ctx.lineTo(x, y); ctx.stroke(); drawn.current = true;
  };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); drawn.current = false; };

  const sign = async () => {
    if (!name.trim()) { setErr("Please type your full name."); return; }
    if (!consent) { setErr("Please tick the consent box to sign electronically."); return; }
    setBusy(true); setErr("");
    try {
      const signature_data = drawn.current ? canvasRef.current.toDataURL("image/png") : null;
      const { data } = await axios.post(`${BASE}/${token}/complete`, { signer_name: name.trim(), signature_data, consent: true });
      setDone(data.message);
    } catch (e) { setErr(e.response?.data?.detail || "Something went wrong. Please try again."); }
    finally { setBusy(false); }
  };

  const decline = async () => {
    setBusy(true); setErr("");
    try { const { data } = await axios.post(`${BASE}/${token}/decline`, { reason: "" }); setDone(data.message); }
    catch (e) { setErr(e.response?.data?.detail || "Something went wrong."); }
    finally { setBusy(false); }
  };

  const Shell = ({ children }) => (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-100 px-4 py-10" style={{ backgroundImage: "radial-gradient(circle at 20% 0%, #10233a, #07111E)" }}>
      <div className="mb-5 flex items-center gap-2 text-gold"><FileSignature size={22} /><span className="font-serif text-xl font-semibold text-white">ApexPath</span></div>
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl sm:p-8" data-testid="sign-page">{children}</div>
      <p className="mt-5 max-w-lg text-center text-xs text-slate-400">This is a secure, single-document link from ApexPath. It does not give access to any other information.</p>
    </div>
  );

  if (err && !info) return <Shell><div className="text-center"><p className="text-sm text-red-600" data-testid="sign-error">{err}</p></div></Shell>;
  if (!info) return <Shell><p className="text-center text-sm text-slate-500">Loading…</p></Shell>;

  if (done) return (
    <Shell>
      <div className="text-center" data-testid="sign-success">
        <CheckCircle2 size={48} className="mx-auto text-emerald-500" />
        <h1 className="mt-3 font-serif text-2xl font-semibold text-navy">All done</h1>
        <p className="mt-2 text-sm text-slate-600">{done}</p>
      </div>
    </Shell>
  );

  const closed = ["Signed", "Declined", "Expired", "Cancelled"].includes(info.status);
  if (closed) return (
    <Shell>
      <div className="text-center">
        <h1 className="font-serif text-2xl font-semibold text-navy">{info.doc_type}</h1>
        <p className="mt-3 text-sm text-slate-600">This document is no longer awaiting signature (status: <b>{info.status}</b>). Please contact ApexPath if you have any questions.</p>
      </div>
    </Shell>
  );

  if (info.method !== "esign") return (
    <Shell>
      <div className="text-center">
        <h1 className="font-serif text-2xl font-semibold text-navy">{info.doc_type}</h1>
        <p className="mt-3 text-sm text-slate-600">ApexPath will arrange for this document to be signed on paper. No action is needed here.</p>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <h1 className="font-serif text-2xl font-semibold text-navy">{info.doc_name || info.doc_type}</h1>
      <p className="mt-1 text-sm text-slate-500">Prepared for <b>{info.client_name}</b> by ApexPath. Please read the full document below before signing.</p>
      {info.message && <p className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">{info.message}</p>}

      {docHtml ? (
        <div className="mt-4">
          <div onScroll={(e) => { const el = e.target; if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setRead(true); }}
            data-testid="sign-document-body"
            className="max-h-72 overflow-y-auto rounded-lg border border-slate-300 bg-white p-4 text-sm leading-relaxed text-slate-800"
            dangerouslySetInnerHTML={{ __html: docHtml }} />
          <p className="mt-1 text-xs text-slate-400">Scroll to the end of the document to enable signing.</p>
        </div>
      ) : info.has_original ? (
        <a href={`${BASE}/${token}/document`} target="_blank" rel="noreferrer" data-testid="sign-view-doc" onClick={() => setRead(true)}
          className="mt-4 flex items-center justify-center gap-2 rounded-lg border border-navy/20 bg-white py-2.5 text-sm font-medium text-navy hover:bg-slate-50">
          <FileText size={16} /> Open and read the document first
        </a>
      ) : null}

      <div className="mt-5">
        <label className="text-sm font-medium text-slate-700">Your full name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="sign-name"
          className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-slate-900" placeholder="e.g. Jane Doe" />
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-slate-700">Draw your signature</label>
          <button onClick={clear} data-testid="sign-clear" className="flex items-center gap-1 text-xs text-slate-500 hover:text-navy"><Eraser size={13} /> Clear</button>
        </div>
        <canvas ref={canvasRef} width={560} height={180} data-testid="sign-canvas"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          className="mt-1.5 w-full touch-none rounded-lg border-2 border-dashed border-slate-300 bg-slate-50" style={{ height: 160 }} />
        <p className="mt-1 text-xs text-slate-400">Optional — if you leave this blank we’ll use your typed name as your signature.</p>
      </div>

      <label className="mt-4 flex items-start gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="sign-consent" className="mt-0.5 h-4 w-4" />
        I agree to sign this document electronically and confirm the information is correct.
      </label>

      {err && <p className="mt-3 text-sm text-red-600" data-testid="sign-form-error">{err}</p>}

      <button onClick={sign} disabled={busy || ((docHtml || info.has_original) && !read)} data-testid="sign-submit"
        className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg bg-gold py-3 font-semibold text-navy disabled:opacity-60">
        <ShieldCheck size={18} /> {busy ? "Signing…" : ((docHtml || info.has_original) && !read ? "Please read the document first" : "Sign document")}
      </button>
      <button onClick={decline} disabled={busy} data-testid="sign-decline" className="mt-2 w-full text-xs text-slate-400 hover:text-red-500">I don’t want to sign</button>
    </Shell>
  );
}
